import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { serviceHistoryApi } from '@/lib/api/service-history.api';

import type {
  ServiceHistoryModel,
  ServiceVehicleDetail,
  UpdateServiceHistorySettingsDto,
} from '@repo/shared-types';

const VEHICLES_KEY = ['service-history-vehicles'];
const BY_REGISTRATION_KEY = ['service-history-by-registration'];
const TOKENS_KEY = ['service-history-tokens'];
const vehicleKey = (id: string) => ['service-history-vehicle', id];

export const useServiceVehicles = (params: { search?: string; page: number; limit: number }) =>
  useQuery({
    queryKey: [...VEHICLES_KEY, params],
    queryFn: () => serviceHistoryApi.listVehicles(params),
  });

export const useServiceVehicle = (id: string | undefined) =>
  useQuery({
    queryKey: vehicleKey(id ?? ''),
    queryFn: () => serviceHistoryApi.getVehicle(id as string),
    enabled: !!id,
  });

/** Service history for a stock car, matched by registration. */
export const useServiceHistoryForRegistration = (registration: string | null, enabled: boolean) =>
  useQuery({
    queryKey: [...BY_REGISTRATION_KEY, registration],
    queryFn: () => serviceHistoryApi.findByRegistration(registration as string),
    enabled: enabled && !!registration,
    retry: false,
  });

export const useReadServiceFiles = () =>
  useMutation({
    mutationFn: ({ files, model }: { files: File[]; model: ServiceHistoryModel }) =>
      serviceHistoryApi.readFiles(files, model),
  });

/**
 * Shared handlers for anything that changes a vehicle. On success the vehicle
 * page is updated straight away; on error it is refetched, because files and
 * failed runs are saved even when the AI fails.
 */
const useVehicleHandlers = (id?: string) => {
  const queryClient = useQueryClient();
  return {
    onSuccess: (vehicle: ServiceVehicleDetail) => {
      queryClient.setQueryData(vehicleKey(vehicle.id), vehicle);
      queryClient.invalidateQueries({ queryKey: VEHICLES_KEY });
      queryClient.invalidateQueries({ queryKey: BY_REGISTRATION_KEY });
    },
    onError: () => {
      if (id) queryClient.invalidateQueries({ queryKey: vehicleKey(id) });
    },
  };
};

export const useSaveServiceDraft = () =>
  useMutation({
    mutationFn: ({ draftId, registration }: { draftId: string; registration: string }) =>
      serviceHistoryApi.saveDraft(draftId, registration),
    ...useVehicleHandlers(),
  });

export const useAddServiceDocuments = (id: string) =>
  useMutation({
    mutationFn: ({ files, model }: { files: File[]; model: ServiceHistoryModel }) =>
      serviceHistoryApi.addDocuments(id, files, model),
    ...useVehicleHandlers(id),
  });

export const useRemoveServiceDocument = (id: string) =>
  useMutation({
    mutationFn: (documentId: string) => serviceHistoryApi.removeDocument(id, documentId),
    ...useVehicleHandlers(id),
  });

export const useRegenerateServiceSummary = (id: string) =>
  useMutation({
    mutationFn: (model: ServiceHistoryModel) => serviceHistoryApi.regenerate(id, model),
    ...useVehicleHandlers(id),
  });

export const useDeleteServiceVehicle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceHistoryApi.deleteVehicle(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: vehicleKey(id) });
      queryClient.invalidateQueries({ queryKey: VEHICLES_KEY });
      queryClient.invalidateQueries({ queryKey: BY_REGISTRATION_KEY });
    },
  });
};

export const useEditServiceSummary = (id: string) =>
  useMutation({
    mutationFn: (summary: string) => serviceHistoryApi.editSummary(id, summary),
    ...useVehicleHandlers(id),
  });

const SETTINGS_KEY = ['service-history-settings'];

export const useServiceHistorySettings = () =>
  useQuery({ queryKey: SETTINGS_KEY, queryFn: () => serviceHistoryApi.getSettings() });

export const useUpdateServiceHistorySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: UpdateServiceHistorySettingsDto) => serviceHistoryApi.updateSettings(dto),
    onSuccess: (settings) => queryClient.setQueryData(SETTINGS_KEY, settings),
  });
};

export const useServiceHistoryTokens = () =>
  useQuery({ queryKey: TOKENS_KEY, queryFn: () => serviceHistoryApi.getTokens() });

export const useCreateServiceHistoryToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => serviceHistoryApi.createToken(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TOKENS_KEY }),
  });
};

export const useRevokeServiceHistoryToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceHistoryApi.revokeToken(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TOKENS_KEY }),
  });
};
